import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {hpcAttrs} from '@github-ui/hpc'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {replaceDateTokens} from '@github-ui/issue-url-helper'
import {useQuery} from '@github-ui/react-query'
import {Link as RouterLink, useLocation, useSearchParams} from '@github-ui/react-router'
import type {SavedViewIssuesRenderer} from '@github-ui/saved-views'
import {usePreviewQueryContext} from '@github-ui/saved-views/contexts/PreviewQueryContext'
import {addStateToQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {addTypeFilterToQuery} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import {
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '@github-ui/shared-dashboard-lists/shared/consts/sorting-consts'
import {useNavigate} from '@github-ui/use-navigate'
import {Banner, Pagination} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {RepoIssuesBulkJobInfo} from '../components/RepoIssuesBulkJobInfo'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {useOptimisticRepoIssuesBulkUpdates} from '../helpers/repo-issues-bulk-optimistic-update'
import {createRepoRowNodeLoader, shouldFetchReactions} from '../hooks/repo-issues-row-node'
import {useIssuesDashboardLiveUpdates} from '../hooks/use-issues-dashboard-live-updates'
import {useRepoIssuesBulkActionState} from '../hooks/use-repo-issues-bulk-action-state'
import {useRowNodeOverlay} from '../hooks/use-row-node-overlay'
import {issuesLayoutRoute} from './issues-layout-route'
import {savedViewIssuesQueryOptions} from './saved-view-issues-query'

export const TanStackSavedViewIssues: SavedViewIssuesRenderer = ({owner, repo, query, preheatEnabled}) => {
  const previewQuery = usePreviewQueryContext()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const [searchParams] = useSearchParams()
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const activeQuery = previewQuery?.currentQuery ?? query
  const resolvedQuery = replaceDateTokens(activeQuery)
  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const resultsQuery = useQuery(savedViewIssuesQueryOptions({owner, repo, query: resolvedQuery, page}))
  const {refetch: refetchResults} = resultsQuery
  const listRows = useMemo(() => repoIssuesToListItems(resultsQuery.data?.results ?? []), [resultsQuery.data?.results])
  const fetchReactions = shouldFetchReactions(resolvedQuery)
  const rowNodeLoader = useMemo(
    () => createRepoRowNodeLoader(owner, repo, fetchReactions),
    [owner, repo, fetchReactions],
  )
  const rowNodeQueryScope = `${owner}/${repo}:fetch-reactions=${fetchReactions}`
  const rowNodeIssues = useRowNodeOverlay({
    rows: listRows,
    listUpdatedAt: resultsQuery.dataUpdatedAt,
    loadRowNode: rowNodeLoader.loadRowNode,
    queryKeyScope: rowNodeQueryScope,
  })
  const issues = useOptimisticRepoIssuesBulkUpdates(rowNodeIssues)
  const liveUpdates = useIssuesDashboardLiveUpdates(listRows, rowNodeQueryScope)
  const pageInfo = resultsQuery.data?.pageInfo ?? {currentPage: page, totalPages: 1, totalCount: 0}
  const repositoryId = layoutPayload && 'repositoryId' in layoutPayload ? layoutPayload.repositoryId : ''
  const viewerCanBulkUpdate =
    preheatEnabled === true &&
    layoutPayload != null &&
    'canBulkUpdate' in layoutPayload &&
    layoutPayload.canBulkUpdate === true
  const refreshIssues = useCallback(async () => refetchResults(), [refetchResults])
  const {
    bulkJobId,
    clearBulkJobId,
    selection,
    notificationStateCommands,
    bulkActionError,
    clearBulkActionError,
    onBulkJobCompleted,
    onBulkJobError,
  } = useRepoIssuesBulkActionState({
    owner,
    repo,
    currentPage: pageInfo.currentPage,
    query: resolvedQuery,
    repositoryId,
    currentUserId: layoutPayload?.currentUser?.id ?? '',
    viewerCanBulkUpdate,
    totalCount: pageInfo.totalCount,
    issues,
    hasProjectsEnabled: layoutPayload?.hasProjectsEnabled ?? false,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
    refreshIssues,
  })
  const resultsRef = useRef<HTMLDivElement>(null)
  const focusAfterPaginationRef = useRef(false)
  const {emptyListState} = useScreenReaderAnnouncements(
    'issues',
    resultsQuery.isFetching,
    pageInfo.currentPage,
    activeQuery,
    'created',
    pageInfo.totalCount,
    pageInfo.totalPages,
    resultsQuery.error?.message,
    undefined,
    {...defaultSortingOptions, ...issuesDashboardSortingOptions},
  )

  useEffect(() => {
    if (resultsQuery.isFetching || !focusAfterPaginationRef.current) return
    focusAfterPaginationRef.current = false
    resultsRef.current?.focus()
  }, [page, resultsQuery.isFetching])

  useEffect(() => {
    focusAfterPaginationRef.current = false
  }, [activeQuery])

  const updateQuery = useCallback(
    (nextQuery: string) => {
      if (previewQuery) {
        previewQuery.onQueryChange(nextQuery)
        return
      }

      navigate(`${pathname}?q=${encodeURIComponent(nextQuery)}`, {preventAutofocus: true})
    },
    [navigate, pathname, previewQuery],
  )
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: activeQuery,
    applySectionFilter: updateQuery,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
  })

  return (
    <>
      {liveUpdates}
      <RepoIssuesBulkJobInfo
        jobId={bulkJobId}
        enabled={viewerCanBulkUpdate}
        clearJobId={clearBulkJobId}
        refreshIssues={refreshIssues}
        onCompleted={onBulkJobCompleted}
        onError={onBulkJobError}
      />
      {bulkActionError ? (
        <Banner className="mt-3" variant="critical" title={bulkActionError} onDismiss={clearBulkActionError} />
      ) : null}
      <div ref={resultsRef} role="group" aria-label="Saved view issues results" tabIndex={-1} {...hpcAttrs()}>
        <IssuesList
          issues={issues}
          pageInfo={pageInfo}
          compactMode={false}
          onCompactModeChange={() => {}}
          defaultSortingKey="created"
          separateAgentAssignees
          filterActions={filterActions}
          selection={selection}
          onSortingItemSelect={sort => updateQuery(addSortToQuery(activeQuery, sort))}
          sortingItemSelected={getSortFromQuery(activeQuery)}
          onStateFilterSelect={state => updateQuery(addStateToQuery(activeQuery, state))}
          getIssueTypeHref={issueType => `?q=${encodeURIComponent(addTypeFilterToQuery(activeQuery, issueType))}`}
          issueTypeLinkComponent={RouterLink}
          emptyListState={emptyListState}
          isLoading={resultsQuery.isLoading}
        />
        {notificationStateCommands}
        {pageInfo.totalPages > 1 && (
          <Pagination
            pageCount={pageInfo.totalPages}
            currentPage={pageInfo.currentPage}
            hrefBuilder={nextPage => `?q=${encodeURIComponent(activeQuery)}&page=${nextPage}`}
            onPageChange={(event, nextPage) => {
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

              event.preventDefault()
              focusAfterPaginationRef.current = true
              const next = new URLSearchParams(searchParams)
              next.set('page', String(nextPage))
              navigate(`${pathname}?${next}`, {preventAutofocus: true})
            }}
          />
        )}
      </div>
    </>
  )
}
